class BBHomepageCatalogue {
  constructor(section) {
    this.section = section;

    this.groups = Array.from(
      section.querySelectorAll('[data-bb-catalogue-group]')
    );

    this.rootMargin =
      section.dataset.loadDistance || '700px 0px';

    this.destroyed = false;

    this.initialise();
  }

  initialise() {
    this.groups.forEach((group) => {
      this.initialiseGroup(group);
    });
  }

  initialiseGroup(group) {
    if (
      group.dataset.bbCatalogueInitialised === 'true'
    ) {
      return;
    }

    group.dataset.bbCatalogueInitialised = 'true';

    const state = {
      group,
      grid: group.querySelector(
        '[data-bb-catalogue-grid]'
      ),
      sentinel: group.querySelector(
        '[data-bb-catalogue-sentinel]'
      ),
      status: group.querySelector(
        '[data-bb-catalogue-status]'
      ),
      button: group.querySelector(
        '[data-bb-load-more]'
      ),
      emptyMessage: group.querySelector(
        '[data-bb-catalogue-empty]'
      ),
      errorMessage: group.querySelector(
        '[data-bb-catalogue-error]'
      ),
      collectionUrl:
        group.dataset.collectionUrl || '',
      nextUrl: '',
      loading: false,
      complete: false,
      failed: false,
      controller: null,
      observer: null,
      timeoutId: null
    };

    group.bbCatalogueState = state;

    if (!state.grid || !state.collectionUrl) {
      this.showError(
        state,
        'Select a valid collection for this catalogue block.'
      );

      return;
    }

    state.nextUrl = this.createSectionUrl(
      state.collectionUrl
    );

    this.bindButton(state);
    this.observeGroup(state);
  }

  bindButton(state) {
    if (!state.button) {
      return;
    }

    state.button.addEventListener(
      'click',
      (event) => {
        event.preventDefault();

        if (state.loading || state.complete) {
          return;
        }

        state.failed = false;
        this.loadNextBatch(state);
      }
    );
  }

  observeGroup(state) {
    if (!state.sentinel) {
      this.loadNextBatch(state);
      return;
    }

    if (!('IntersectionObserver' in window)) {
      this.showButton(state);
      return;
    }

    state.observer = new IntersectionObserver(
      (entries) => {
        const isVisible = entries.some(
          (entry) => entry.isIntersecting
        );

        if (
          isVisible &&
          !state.loading &&
          !state.complete &&
          !state.failed
        ) {
          this.loadNextBatch(state);
        }
      },
      {
        root: null,
        rootMargin: this.rootMargin,
        threshold: 0.01
      }
    );

    state.observer.observe(state.sentinel);
  }

  createSectionUrl(sourceUrl) {
    const url = new URL(
      sourceUrl,
      window.location.origin
    );

    url.searchParams.delete('sections');

    url.searchParams.set(
      'section_id',
      'bb-catalogue-batch'
    );

    if (!url.searchParams.has('page')) {
      url.searchParams.set('page', '1');
    }

    return url.toString();
  }

  async loadNextBatch(state) {
    if (
      this.destroyed ||
      state.loading ||
      state.complete ||
      !state.nextUrl
    ) {
      return;
    }

    state.loading = true;
    state.failed = false;

    state.group.classList.add('is-loading');

    this.hideError(state);
    this.setStatus(state, 'Loading products…');
    this.setButtonLoading(state);

    if (state.controller) {
      state.controller.abort();
    }

    state.controller = new AbortController();

    let requestTimedOut = false;

    state.timeoutId = window.setTimeout(() => {
      requestTimedOut = true;

      if (state.controller) {
        state.controller.abort();
      }
    }, 12000);

    try {
      const response = await fetch(
        state.nextUrl,
        {
          method: 'GET',
          headers: {
            Accept: 'text/html',
            'X-Requested-With': 'XMLHttpRequest'
          },
          credentials: 'same-origin',
          signal: state.controller.signal
        }
      );

      if (!response.ok) {
        throw new Error(
          `Request failed with status ${response.status}`
        );
      }

      const html = await response.text();

      if (!html.trim()) {
        throw new Error(
          'Shopify returned an empty response.'
        );
      }

      const batch = this.parseBatch(html);

      if (!batch) {
        console.error(
          'Unexpected catalogue response:',
          html
        );

        throw new Error(
          'The catalogue batch element was not found.'
        );
      }

      const batchError = batch.querySelector(
        '[data-bb-catalogue-batch-error]'
      );

      if (batchError) {
        throw new Error(
          batchError.textContent.trim() ||
            'Collection context was unavailable.'
        );
      }

      const addedCount =
        this.appendBatchItems(state, batch);

      const nextUrlElement = batch.querySelector(
        '[data-bb-next-url]'
      );

      const completeElement = batch.querySelector(
        '[data-bb-catalogue-complete]'
      );

      if (nextUrlElement) {
        const rawNextUrl =
          nextUrlElement.dataset.bbNextUrl || '';

        if (rawNextUrl) {
          state.nextUrl = this.createSectionUrl(
            rawNextUrl
          );

          state.complete = false;
        } else {
          state.nextUrl = '';
          state.complete = true;
        }
      } else if (completeElement) {
        state.nextUrl = '';
        state.complete = true;
      } else {
        state.nextUrl = '';
        state.complete = true;
      }

      if (addedCount === 0) {
        state.nextUrl = '';
        state.complete = true;
      }

      if (state.complete) {
        this.finishGroup(state);
      } else {
        this.setStatus(state, '');
        this.showButton(state);
      }
    } catch (error) {
      state.failed = true;

      console.error(
        'BiltongBoys catalogue loading error:',
        error
      );

      const message =
        requestTimedOut ||
        error.name === 'AbortError'
          ? 'The product request took too long. Please try again.'
          : 'Products could not be loaded. Please try again.';

      this.showError(state, message);
      this.showButton(state, 'Try again');
    } finally {
      if (state.timeoutId) {
        window.clearTimeout(state.timeoutId);
        state.timeoutId = null;
      }

      state.loading = false;
      state.group.classList.remove('is-loading');
      state.controller = null;

      if (
        !state.complete &&
        !state.failed
      ) {
        this.resetButton(state);
      }
    }
  }

  parseBatch(html) {
    const parser = new DOMParser();

    const parsedDocument =
      parser.parseFromString(
        html,
        'text/html'
      );

    return parsedDocument.querySelector(
      '[data-bb-catalogue-batch]'
    );
  }

  appendBatchItems(state, batch) {
    const itemsContainer = batch.querySelector(
      '[data-bb-catalogue-batch-items]'
    );

    if (!itemsContainer) {
      return 0;
    }

    const incomingCards = Array.from(
      itemsContainer.querySelectorAll(
        ':scope > [data-bb-catalogue-card]'
      )
    );

    if (!incomingCards.length) {
      return 0;
    }

    const existingVariantIds = new Set(
      Array.from(
        state.grid.querySelectorAll(
          '[data-variant-id]'
        )
      )
        .map((card) => card.dataset.variantId)
        .filter(Boolean)
    );

    const fragment =
      document.createDocumentFragment();

    const addedCards = [];

    incomingCards.forEach((card) => {
      const variantId =
        card.dataset.variantId || '';

      if (
        variantId &&
        existingVariantIds.has(variantId)
      ) {
        return;
      }

      const importedCard =
        document.importNode(card, true);

      fragment.appendChild(importedCard);
      addedCards.push(importedCard);

      if (variantId) {
        existingVariantIds.add(variantId);
      }
    });

    if (!addedCards.length) {
      return 0;
    }

    state.grid.appendChild(fragment);

    this.animateCards(addedCards);
    this.initialiseImages(addedCards);

    document.dispatchEvent(
      new CustomEvent(
        'bb:catalogue:loaded',
        {
          detail: {
            root: state.grid,
            cards: addedCards
          }
        }
      )
    );

    return addedCards.length;
  }

  animateCards(cards) {
    cards.forEach((card, index) => {
      card.classList.add(
        'bb-catalogue-card-enter'
      );

      card.style.setProperty(
        '--bb-card-delay',
        `${Math.min(index * 35, 280)}ms`
      );

      window.requestAnimationFrame(() => {
        card.classList.add(
          'bb-catalogue-card-enter-active'
        );
      });

      window.setTimeout(() => {
        card.classList.remove(
          'bb-catalogue-card-enter',
          'bb-catalogue-card-enter-active'
        );

        card.style.removeProperty(
          '--bb-card-delay'
        );
      }, 700);
    });
  }

  initialiseImages(cards) {
    cards.forEach((card) => {
      card.querySelectorAll('img').forEach(
        (image) => {
          if (image.complete) {
            image.classList.add('is-loaded');
            return;
          }

          image.addEventListener(
            'load',
            () => {
              image.classList.add(
                'is-loaded'
              );
            },
            {
              once: true
            }
          );
        }
      );
    });
  }

  finishGroup(state) {
    state.complete = true;
    state.failed = false;
    state.nextUrl = '';

    state.group.classList.add(
      'is-complete'
    );

    this.setStatus(state, '');
    this.hideButton(state);

    if (state.sentinel) {
      state.sentinel.hidden = true;
    }

    if (state.observer) {
      state.observer.disconnect();
      state.observer = null;
    }

    if (
      state.emptyMessage &&
      state.grid.children.length === 0
    ) {
      state.emptyMessage.hidden = false;
    }
  }

  setStatus(state, message) {
    if (!state.status) {
      return;
    }

    state.status.textContent = message;
    state.status.hidden = !message;
  }

  showError(state, message) {
    state.group.classList.add(
      'has-error'
    );

    if (state.errorMessage) {
      state.errorMessage.textContent =
        message;

      state.errorMessage.hidden = false;
    }

    this.setStatus(state, '');
  }

  hideError(state) {
    state.group.classList.remove(
      'has-error'
    );

    if (state.errorMessage) {
      state.errorMessage.textContent = '';
      state.errorMessage.hidden = true;
    }
  }

  showButton(
    state,
    label = 'Load more products'
  ) {
    if (!state.button) {
      return;
    }

    state.button.hidden = false;
    state.button.disabled = false;
    state.button.textContent = label;
  }

  hideButton(state) {
    if (!state.button) {
      return;
    }

    state.button.hidden = true;
    state.button.disabled = false;
  }

  setButtonLoading(state) {
    if (!state.button) {
      return;
    }

    state.button.hidden = false;
    state.button.disabled = true;
    state.button.textContent =
      'Loading products…';
  }

  resetButton(state) {
    if (!state.button) {
      return;
    }

    state.button.disabled = false;

    if (!state.complete) {
      state.button.hidden = false;
      state.button.textContent =
        'Load more products';
    }
  }

  destroy() {
    this.destroyed = true;

    this.groups.forEach((group) => {
      const state =
        group.bbCatalogueState;

      if (!state) {
        return;
      }

      if (state.timeoutId) {
        window.clearTimeout(state.timeoutId);
      }

      if (state.observer) {
        state.observer.disconnect();
      }

      if (state.controller) {
        state.controller.abort();
      }
    });
  }
}

function initialiseBBHomepageCatalogues(
  root = document
) {
  const sections = [];

  if (
    root.matches?.(
      '[data-bb-homepage-catalogue]'
    )
  ) {
    sections.push(root);
  }

  sections.push(
    ...root.querySelectorAll(
      '[data-bb-homepage-catalogue]'
    )
  );

  sections.forEach((section) => {
    if (
      section.dataset
        .bbHomepageCatalogueInitialised ===
      'true'
    ) {
      return;
    }

    section.dataset
      .bbHomepageCatalogueInitialised =
      'true';

    section.bbHomepageCatalogue =
      new BBHomepageCatalogue(section);
  });
}

if (document.readyState === 'loading') {
  document.addEventListener(
    'DOMContentLoaded',
    () => {
      initialiseBBHomepageCatalogues();
    }
  );
} else {
  initialiseBBHomepageCatalogues();
}

document.addEventListener(
  'shopify:section:load',
  (event) => {
    initialiseBBHomepageCatalogues(
      event.target
    );
  }
);

document.addEventListener(
  'shopify:section:unload',
  (event) => {
    const catalogue =
      event.target.matches?.(
        '[data-bb-homepage-catalogue]'
      )
        ? event.target
        : event.target.querySelector(
            '[data-bb-homepage-catalogue]'
          );

    catalogue?.bbHomepageCatalogue?.destroy();
  }
);